import React, { useState, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useAllEmployeeDetails } from "../../hooks/useEmployees";
import { usePaysliplist } from "../../hooks/usePayslip";
import {
  format
} from "date-fns";
import {
  Box,
  Typography,
  Container,
  Paper,
  Grid,
  Stack,
  TextField,
  InputAdornment,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Fade,
} from "@mui/material";
import { Select, MenuItem, FormControl } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";

import EmployeePayrollDetails from "./EmployeePayrollDetails";
import { TABLE_PAPER_SX, HEAD_CELL_SX, BODY_CELL_SX, TABLE_ROW_SX, AVATAR_SX } from "../common/tableStyles";
import TableCard from "../common/TableCard";
import { SELECTED_MENU_PROPS } from "../StyledComponents/StyledComponent";

// Chart Colors
const COLORS = {
  paid: "#4f46e5",
  pending: "#f97316",
  unpaid: "#eab308",
};

// ---- FORTHEYE soft-card tokens (shared with the rest of the portal) ----
const CARD_SX = {
  p: { xs: 2, sm: 2.5, md: 3 },
  borderRadius: "22px",
  bgcolor: "#fff",
  border: "1px solid #eef0f4",
  boxShadow: "0 6px 24px rgba(17,24,39,0.05)",
};
const ACCENT = "#2f6bff";


const SalaryEmp = () => {
  const location = useLocation();
  const [selectedEmp, setSelectedEmp] = useState(location.state?.selectedEmployee || null);

  // Table States
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showSalary, setShowSalary] = useState({});
  const [filterDepartment, setFilterDepartment] = useState("All");
  const [filterPayrollStatus, setFilterPayrollStatus] = useState("All");
  // Period Selection States
  const [selectedMonth, setSelectedMonth] = useState(format(new Date(), "MMMM"));
  const [selectedYear, setSelectedYear] = useState(format(new Date(), "yyyy"));

  // Years for filter (current and previous)
  const years = useMemo(() => {
    const current = new Date().getFullYear();
    return [String(current), String(current - 1), String(current - 2)];
  }, []);

  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const { data: emp = [] } = useAllEmployeeDetails();

  const { data: payslipData = [] } = usePaysliplist({
    month: selectedMonth,
    year: selectedYear,
    page_size: 1000
  });

  const periodPayslips = useMemo(() => {
    const rows = Array.isArray(payslipData) ? payslipData : (payslipData?.results || []);
    const priority = { OUTDATED: 0, DRAFT: 1, PUBLISHED: 2 };
    const byEmployee = new Map();

    rows.forEach((payslip) => {
      if (payslip.month !== selectedMonth || String(payslip.year) !== selectedYear) return;
      const employeeNo = String(payslip.employee_no || '');
      if (!employeeNo) return;
      const current = byEmployee.get(employeeNo);
      const status = payslip.status || 'DRAFT';
      const currentStatus = current?.status || 'OUTDATED';
      if (!current || (priority[status] ?? 0) > (priority[currentStatus] ?? 0)) {
        byEmployee.set(employeeNo, { ...payslip, status });
      }
    });

    return byEmployee;
  }, [payslipData, selectedMonth, selectedYear]);

  // Calculate Payment Status Data Dynamically
  const dynamicPaymentStatusData = useMemo(() => {
    const total = emp.length || 0;

    const periodRows = [...periodPayslips.values()];
    const paidCount = periodRows.filter((p) => p.status === 'PUBLISHED' || p.status === 'DRAFT').length;
    // Payslip publication is a document workflow, not a payment workflow. Under
    // the current business rule, any generated current payslip counts as Paid.
    const pendingCount = 0;
    const unpaidCount = Math.max(0, total - paidCount - pendingCount);

    return [
      { name: "Successfully Paid", value: paidCount, color: COLORS.paid },
      { name: "Pending", value: pendingCount, color: COLORS.pending },
      { name: "Unpaid", value: unpaidCount, color: COLORS.unpaid },
    ];
  }, [emp, periodPayslips]);



  // Filter Employees
  const filteredEmployees = useMemo(() => {
    return emp.filter((employee) => {
      const matchesSearch =
        employee.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        employee.employee_no.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesDept =
        filterDepartment === "All" || employee.department === filterDepartment;
      const payslip = periodPayslips.get(String(employee.employee_no));
      const payrollStatus = payslip?.status === 'PUBLISHED' || payslip?.status === 'DRAFT'
        ? 'Paid'
        : 'Unpaid';
      const matchesPayrollStatus =
        filterPayrollStatus === "All" || payrollStatus === filterPayrollStatus;
      return matchesSearch && matchesDept && matchesPayrollStatus;
    });
  }, [emp, searchQuery, filterDepartment, filterPayrollStatus, periodPayslips]);

  // Unique departments for filter
  const departments = useMemo(() => {
    const depts = emp.map((e) => e.department).filter(Boolean);
    return ["All", ...new Set(depts)];
  }, [emp]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const toggleSalaryVisibility = (e, empId) => {
    e.stopPropagation();
    setShowSalary((prev) => ({
      ...prev,
      [empId]: !prev[empId],
    }));
  };

  const clearSelection = () => {
    setSelectedEmp(null);
  };

  // If an employee is selected, show the Deep Dive view
  if (selectedEmp) {
    return (
      <EmployeePayrollDetails
        selectedEmp={selectedEmp}
        month={selectedMonth}
        year={selectedYear}
        onBack={clearSelection}
      />
    );
  }

  const getPayrollStatusChip = (payslip) => {
    const status = payslip?.status === 'PUBLISHED' || payslip?.status === 'DRAFT' ? 'Paid' : 'Unpaid';
    const palette = {
      Paid: { bg: '#eef2ff', fg: COLORS.paid },
      Pending: { bg: '#fff7ed', fg: COLORS.pending },
      Unpaid: { bg: '#fefce8', fg: COLORS.unpaid },
    }[status];
    return (
      <Chip
        label={status}
        size="small"
        sx={{
          bgcolor: palette.bg,
          color: palette.fg,
          fontWeight: 700,
          borderRadius: "8px",
          border: "none",
        }}
      />
    );
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      <Container maxWidth="xl" disableGutters>

        {/* Header card */}
        <Fade in timeout={600}>
          <Paper elevation={0} sx={{ ...CARD_SX, mb: { xs: 2, md: 3 } }}>
            <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", md: "center" }} spacing={2}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.75 }}>
                <Box sx={{ width: 52, height: 52, borderRadius: "16px", bgcolor: `${ACCENT}14`, color: ACCENT, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <AccountBalanceWalletIcon />
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 800, fontSize: { xs: "1.35rem", md: "1.7rem" }, color: "#111827", letterSpacing: "-0.02em", lineHeight: 1.15 }}>
                    Salary View
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#6b7280" }}>
                    Payroll
                  </Typography>
                </Box>
              </Box>
            </Stack>
          </Paper>
        </Fade>

        {/* Top Charts Dashboard */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* Payment Status (Donut) */}
          <Grid item xs={12}>
            <Fade in timeout={600}>
              <Paper elevation={0} sx={{ ...CARD_SX, height: "100%" }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 1 }}>
                  <Typography variant="h6" fontWeight={700} color="#111827">Payment Status</Typography>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Select
                      value={selectedMonth}
                      onChange={(e) => { setSelectedMonth(e.target.value); setPage(0); }}
                      size="small"
                      sx={{ borderRadius: "12px", height: 32, fontSize: '0.75rem', minWidth: 100, bgcolor: "#f8fafc" }}
                      MenuProps={SELECTED_MENU_PROPS}
                    >
                      {months.map(m => <MenuItem key={m} value={m} sx={{ fontSize: '0.75rem' }}>{m}</MenuItem>)}
                    </Select>
                    <Select
                      value={selectedYear}
                      onChange={(e) => { setSelectedYear(e.target.value); setPage(0); }}
                      size="small"
                      sx={{ borderRadius: "12px", height: 32, fontSize: '0.75rem', bgcolor: "#f8fafc" }}
                      MenuProps={SELECTED_MENU_PROPS}
                    >
                      {years.map(y => <MenuItem key={y} value={y} sx={{ fontSize: '0.75rem' }}>{y}</MenuItem>)}
                    </Select>
                  </Box>
                </Box>
                <Box
                  role="note"
                  sx={{
                    px: 1.5,
                    py: 1,
                    mb: 1,
                    borderRadius: "10px",
                    bgcolor: "#eff6ff",
                    border: "1px solid #dbeafe",
                  }}
                >
                  <Typography variant="caption" sx={{ color: "#1e40af", fontWeight: 600 }}>
                    Current rule: Paid means a payslip has been generated, whether Draft or Published. Unpaid means no current payslip exists for the selected month. Publishing controls payslip visibility only; it does not determine salary payment.
                  </Typography>
                </Box>
                {/* Inline stacked bar (replaces the donut) */}
                <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 2, sm: 4 }, flexWrap: "wrap", py: 2 }}>
                  <Box sx={{ minWidth: 96 }}>
                    <Typography variant="h4" fontWeight={800} color="#111827" sx={{ lineHeight: 1 }}>{emp.length}</Typography>
                    <Typography variant="caption" color="text.secondary">Total Employees</Typography>
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 240 }}>
                    <Box sx={{ display: "flex", width: "100%", height: 26, borderRadius: 999, overflow: "hidden", bgcolor: "#f1f5f9", boxShadow: "inset 0 1px 2px rgba(0,0,0,0.04)" }}>
                      {dynamicPaymentStatusData.map((item) => {
                        const pct = emp.length > 0 ? (item.value / emp.length) * 100 : 0;
                        if (pct <= 0) return null;
                        return (
                          <Box
                            key={item.name}
                            title={`${item.name}: ${item.value} (${Math.round(pct)}%)`}
                            sx={{ width: `${pct}%`, bgcolor: item.color, height: "100%", transition: "width 0.4s ease" }}
                          />
                        );
                      })}
                    </Box>
                  </Box>
                </Box>
                {/* Legend */}
                <Box sx={{ display: "flex", justifyContent: "flex-start", gap: 2.5, flexWrap: "wrap", mt: 2 }}>
                  {dynamicPaymentStatusData.map((item) => (
                    <Box key={item.name} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: item.color }} />
                      <Typography variant="caption" fontWeight={600}>
                        {emp.length > 0 ? Math.round((item.value / emp.length) * 100) : 0}% {item.name}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Paper>
            </Fade>
          </Grid>

        </Grid>

        {/* Employees Payroll Table Section */}
        <TableCard
          title="Employees Payroll"
          actions={
            <>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <Select
                  value={filterPayrollStatus}
                  onChange={(e) => { setFilterPayrollStatus(e.target.value); setPage(0); }}
                  displayEmpty
                  sx={{ borderRadius: "12px", bgcolor: "#f8fafc", "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e5e7eb" } }}
                  MenuProps={SELECTED_MENU_PROPS}
                >
                  {['All', 'Paid', 'Pending', 'Unpaid'].map((status) => (
                    <MenuItem key={status} value={status}>
                      {status === 'All' ? 'All statuses' : status}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 150 }}>
                <Select
                  value={filterDepartment}
                  onChange={(e) => setFilterDepartment(e.target.value)}
                  displayEmpty
                  startAdornment={<FilterListIcon sx={{ color: "#9ca3af", mr: 1, fontSize: 18 }} />}
                  sx={{ borderRadius: "12px", bgcolor: "#f8fafc", "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e5e7eb" } }}
                  MenuProps={SELECTED_MENU_PROPS}
                >
                  {departments.map((dept) => (
                    <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                placeholder="Search here..."
                size="small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "#9ca3af", fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  minWidth: 220,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    bgcolor: "#f8fafc",
                    "& fieldset": { borderColor: "#e5e7eb" },
                  },
                }}
              />
            </>
          }
          sx={{ p: 0 }}
        >
            {/* Table Content */}
            <TableContainer sx={{ ...TABLE_PAPER_SX, maxHeight: 500, borderRadius: 0, border: 'none' }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={HEAD_CELL_SX}>Full Name & Email</TableCell>
                    <TableCell sx={HEAD_CELL_SX}>Department</TableCell>
                    <TableCell sx={HEAD_CELL_SX}>Period</TableCell>
                    <TableCell sx={HEAD_CELL_SX}>Monthly CTC</TableCell>
                    <TableCell sx={HEAD_CELL_SX}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredEmployees
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((employee) => {
                      const periodPayslip = periodPayslips.get(String(employee.employee_no));
                      const isVisible = showSalary[employee.id] || false;
                      let currentCTC = "0.00";

                      // Monthly CTC always comes from CTCItems — the same single
                      // source of truth the Salary Detail page and the Detailed
                      // Salary Report export use. This list is fed by the `lite`
                      // employees endpoint (LiteEmployeeSerializer), which exposes
                      // it as a flat `monthly_ctc` field (itself read off
                      // ctc_items.monthly_ctc server-side) — not a nested
                      // `ctc_items` array like the full employee-detail endpoint.
                      // Previously this also guessed across gross_pay[0]/
                      // ctc_revision[0] as fallbacks, which read different (and
                      // sometimes differently-ordered) tables and could show a
                      // different figure than the other two screens.
                      if (employee.monthly_ctc) {
                        currentCTC = parseFloat(employee.monthly_ctc).toLocaleString("en-IN", { minimumFractionDigits: 2 });
                      }

                      return (
                        <TableRow
                          key={employee.id}
                          hover
                          onClick={() => setSelectedEmp(employee)}
                          sx={{
                            ...TABLE_ROW_SX,
                            cursor: "pointer",
                          }}
                        >
                          <TableCell sx={BODY_CELL_SX}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                              <Avatar sx={AVATAR_SX}>
                                {employee.name.charAt(0)}
                              </Avatar>
                              <Box>
                                <Typography variant="subtitle2" fontWeight={700} color="#111827">
                                  {employee.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  ID: {employee.employee_no}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>

                          <TableCell sx={BODY_CELL_SX}>
                            <Typography variant="body2" color="#4b5563" fontWeight={500}>
                              {employee.department || "N/A"}
                            </Typography>
                          </TableCell>

                          <TableCell sx={BODY_CELL_SX}>
                            <Typography variant="body2" color="#4b5563" fontWeight={500}>
                              {selectedMonth} {selectedYear}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              Monthly payroll
                            </Typography>
                          </TableCell>

                          <TableCell sx={BODY_CELL_SX}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Typography variant="body2" fontWeight={700} color="#111827">
                                {isVisible ? `₹ ${currentCTC}` : "₹ ••••••"}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={(e) => toggleSalaryVisibility(e, employee.id)}
                                sx={{ color: ACCENT, bgcolor: `${ACCENT}14`, "&:hover": { bgcolor: `${ACCENT}22` } }}
                              >
                                {isVisible ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                              </IconButton>
                            </Box>
                          </TableCell>

                          <TableCell sx={BODY_CELL_SX}>
                            {getPayrollStatusChip(periodPayslip)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  {filteredEmployees.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ textAlign: "center", py: 5 }}>
                        <Typography variant="body1" color="text.secondary">
                          No employees found.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredEmployees.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{ borderTop: "1px solid #f3f4f6" }}
            />
        </TableCard>
      </Container>
    </Box>
  );
};

export default SalaryEmp;
